import { useCallback, useSyncExternalStore } from 'react';
import { readPreferredLocation, subscribeToPreferredLocation } from './index';

/**
 * Reactive read of the preferred location for (alias, BU).
 *
 * Unlike a plain `readPreferredLocation` call, this hook re-renders the
 * consumer when:
 *   - this tab writes a new value via `writePreferredLocation` (e.g. the
 *     profile preferences page) — same-tab subscriber notification
 *   - another tab writes the key — browser `storage` event
 *   - the (alias, businessUnitId) scope changes — snapshot re-read
 *
 * This is what makes Audits/Tasks pick up a preference change made on
 * the profile page without a full refresh.
 */
export function usePreferredLocationValue(
  alias: string,
  businessUnitId: string | undefined,
): string | undefined {
  const subscribe = useCallback((onStoreChange: () => void) => {
    const unsubscribe = subscribeToPreferredLocation(onStoreChange);
    // Cross-tab writes fire the browser storage event (never same-tab).
    window.addEventListener('storage', onStoreChange);
    return () => {
      unsubscribe();
      window.removeEventListener('storage', onStoreChange);
    };
  }, []);

  const getSnapshot = useCallback(
    () => readPreferredLocation(alias, businessUnitId),
    [alias, businessUnitId],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}
